/**
 * useForderungen — Hook für Forderungsmanagement Mietwagen (Haftpflichtfall)
 * CRUD-Operationen für forderungsakten, schadensminderung_eintraege, mahnungen, dokumente
 * 
 * v2: Erweitert um Versicherungs-Kontaktdaten, Abtretung, Mietdauer, Rechnung, WinValue-Referenz
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ─── Typen ───────────────────────────────────────────────
export interface Forderungsakte {
  id: string;
  akten_nr: string | null;
  unfall_datum: string | null;
  unfall_ort: string | null;
  unfallgegner_name: string | null;
  unfallgegner_kennzeichen: string | null;
  versicherung_gegner: string | null;
  schaden_nummer: string | null;
  fahrzeugklasse_beschaedigt: string | null;
  fahrzeugklasse_gemietet: string | null;
  klassengleichheit: boolean;
  mietvertrag_nr: string | null;
  mietbeginn: string | null;
  mietende: string | null;
  tagessatz: number | null;
  eigenersparnis_prozent: number;
  reparaturdauer_voraussichtlich: number | null;
  reparaturdauer_tatsaechlich: number | null;
  totalschaden: boolean;
  wiederbeschaffungsdauer: number | null;
  anspruchsgrundlage: string;
  gesamtbetrag: number;
  status: 'offen' | 'in_bearbeitung' | 'abgerechnet' | 'strittig' | 'gerichtlich' | 'erledigt';
  mahnstufe: 'keine' | '1_mahnung' | '2_mahnung' | 'inkasso' | 'anwalt' | 'gerichtlich';
  verjährungsfrist: string | null;
  risiko_einschaetzung: 'stark' | 'mittel' | 'gefaehrdet' | null;
  pruefung_abgeschlossen: boolean;
  pruefung_daten: Record<string, any>;
  akte_id: string | null;
  kunde_id: string | null;
  mitarbeiter_id: string | null;
  rentsoft_rechnungs_nr: string | null;
  rentsoft_invoice_id: string | null;
  // Erweiterte Versicherungsdaten (aus WinValue)
  versicherung_adresse: string | null;
  versicherung_plz: string | null;
  versicherung_ort: string | null;
  versicherung_telefon: string | null;
  versicherung_fax: string | null;
  versicherung_email: string | null;
  versicherungsnummer: string | null;
  // Abtretung
  abtretung_status: 'ausstehend' | 'unterzeichnet' | 'versendet' | null;
  abtretung_datum: string | null;
  abtretung_unterschrift_url: string | null;
  // WinValue-Referenz
  winvalue_dossier_id: string | null;
  winvalue_ref: string | null;
  // Rechnung
  rechnung_nr: string | null;
  rechnung_datum: string | null;
  rechnung_betrag: number | null;
  rechnung_pdf_url: string | null;
  // Mietdauer
  mietdauer_tage: number | null;
  // Stage (0–9, via forderungen_stufe2 migration — Issue #562)
  stage: number;
  // Forderungstyp (Issue #586) — DB CHECK (typ IN ('mietfahrzeug', 'werkstatt_auslage', 'autoglas')) NOT NULL
  typ: 'mietfahrzeug' | 'werkstatt_auslage' | 'autoglas';
  // Absender-E-Mail für Forderungsanschreiben (typ-abhängig)
  absender_email: string | null;
  // Fälligkeitsdatum (rechnung_datum + Zahlungsziel, Issue #689)
  faellig_am: string | null;
  // Cross-Link zur verknüpften Akte (aus forderungsakten_vollstaendig view — Issue #601)
  linked_akte_id?: string | null;
  akte_typ?: string | null;
  schadennummer?: string | null;
  // Rechnungs-FK (für Zahlungseingänge-Tab — Issue #564)
  rechnungs_id?: string | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface SchadensminderungEintrag {
  id: string;
  forderungsakte_id: string;
  datum: string;
  uhrzeit: string | null;
  kategorie: 'alternative_angeboten' | 'reparaturdauer_ueberwacht' | 'kunde_informiert' | 'versicherung_informiert' | 'verzoegerung' | 'sonstiges';
  antwort: boolean | null;
  begruendung: string | null;
  notiz: string | null;
  mitarbeiter_id: string | null;
  mitarbeiter_name: string | null;
  created_at: string;
}

export interface ForderungsMahnung {
  id: string;
  forderungsakte_id: string;
  mahnstufe: string;
  datum: string;
  versand_art: 'email' | 'post' | 'fax' | 'anwalt' | null;
  empfaenger: string | null;
  betreff: string | null;
  inhalt: string | null;
  antwort_erhalten: boolean;
  antwort_datum: string | null;
  antwort_inhalt: string | null;
  mitarbeiter_id: string | null;
  gesendet_an: string | null;
  gesendet_am: string | null;
  status: string | null;
  created_at: string;
}

export interface ForderungsDokument {
  id: string;
  forderungsakte_id: string;
  typ: 'anschreiben' | 'protokoll' | 'mahnung' | 'anlage' | 'sonstiges';
  titel: string;
  inhalt: string | null;
  html_inhalt: string | null;
  datei_url: string | null;
  erstellt_von: string | null;
  created_at: string;
}

// ─── Statistiken ─────────────────────────────────────────
export interface ForderungsStatistik {
  gesamt_offen: number;
  gesamt_betrag: number;
  nach_versicherung: { name: string; betrag: number; anzahl: number }[];
  nach_status: { status: string; anzahl: number }[];
  verjährung_bald: Forderungsakte[];
  durchschnitt_zahlungsdauer: number;
}

// ─── Hook ────────────────────────────────────────────────
export function useForderungen() {
  const [forderungen, setForderungen] = useState<Forderungsakte[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchForderungen = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('forderungsakten')
      .select('*')
      .order('created_at', { ascending: false });
    if (err) setError(err.message);
    else setForderungen(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchForderungen(); }, [fetchForderungen]);

  const createForderung = async (forderung: Partial<Forderungsakte>) => {
    // Berechne Gesamtbetrag
    if (forderung.tagessatz && forderung.mietbeginn && forderung.mietende) {
      const days = Math.ceil((new Date(forderung.mietende).getTime() - new Date(forderung.mietbeginn).getTime()) / 86400000);
      const brutto = days * forderung.tagessatz;
      const ersparnis = brutto * ((forderung.eigenersparnis_prozent || 3.5) / 100);
      forderung.gesamtbetrag = Math.round((brutto - ersparnis) * 100) / 100;
      forderung.mietdauer_tage = days;
    }
    // Berechne Verjährungsfrist (3 Jahre ab Unfalldatum)
    if (forderung.unfall_datum) {
      const d = new Date(forderung.unfall_datum);
      d.setFullYear(d.getFullYear() + 3);
      forderung.verjährungsfrist = d.toISOString().split('T')[0];
    }
    const { data, error: err } = await supabase.from('forderungsakten').insert(forderung).select().single();
    if (err) throw err;
    await fetchForderungen();
    return data;
  };

  const updateForderung = async (id: string, updates: Partial<Forderungsakte>) => {
    updates.updated_at = new Date().toISOString();
    if (updates.tagessatz !== undefined || updates.mietbeginn !== undefined || updates.mietende !== undefined) {
      const current = forderungen.find(f => f.id === id);
      const ts = updates.tagessatz ?? current?.tagessatz ?? 0;
      const mb = updates.mietbeginn ?? current?.mietbeginn;
      const me = updates.mietende ?? current?.mietende;
      const ep = updates.eigenersparnis_prozent ?? current?.eigenersparnis_prozent ?? 3.5;
      if (ts && mb && me) {
        const days = Math.ceil((new Date(me).getTime() - new Date(mb).getTime()) / 86400000);
        const brutto = days * ts;
        updates.gesamtbetrag = Math.round((brutto - brutto * (ep / 100)) * 100) / 100;
        updates.mietdauer_tage = days;
      }
    }
    const { error: err } = await supabase.from('forderungsakten').update(updates).eq('id', id);
    if (err) throw err;
    await fetchForderungen();
  };

  const deleteForderung = async (id: string) => {
    const { error: err } = await supabase.from('forderungsakten').delete().eq('id', id);
    if (err) throw err;
    await fetchForderungen();
  };

  // Schadensminderung
  const getEintraege = async (forderungsId: string) => {
    const { data } = await supabase.from('schadensminderung_eintraege')
      .select('*').eq('forderungsakte_id', forderungsId).order('datum', { ascending: false });
    return data || [];
  };

  const addEintrag = async (eintrag: Partial<SchadensminderungEintrag>) => {
    const { data, error: err } = await supabase.from('schadensminderung_eintraege').insert(eintrag).select().single();
    if (err) throw err;
    return data;
  };

  // Mahnungen
  const getMahnungen = async (forderungsId: string) => {
    const { data } = await supabase.from('forderungs_mahnungen')
      .select('*').eq('forderungsakte_id', forderungsId).order('datum', { ascending: false });
    return data || [];
  };

  const addMahnung = async (mahnung: Partial<ForderungsMahnung>) => {
    const { data, error: err } = await supabase.from('forderungs_mahnungen').insert(mahnung).select().single();
    if (err) throw err;
    // Update Mahnstufe in Forderungsakte
    if (mahnung.forderungsakte_id && mahnung.mahnstufe) {
      await supabase.from('forderungsakten').update({ mahnstufe: mahnung.mahnstufe }).eq('id', mahnung.forderungsakte_id);
    }
    return data;
  };

  // Dokumente
  const getDokumente = async (forderungsId: string) => {
    const { data } = await supabase.from('forderungs_dokumente')
      .select('*').eq('forderungsakte_id', forderungsId).order('created_at', { ascending: false });
    return data || [];
  };

  const addDokument = async (dok: Partial<ForderungsDokument>) => {
    const { data, error: err } = await supabase.from('forderungs_dokumente').insert(dok).select().single();
    if (err) throw err;
    return data;
  };

  // Statistiken berechnen
  const getStatistiken = useCallback((): ForderungsStatistik => {
    const offene = forderungen.filter(f => !['erledigt', 'abgerechnet'].includes(f.status));
    const nachVers: Record<string, { betrag: number; anzahl: number }> = {};
    const nachStatus: Record<string, number> = {};
    const heute = new Date();
    const in3Monaten = new Date();
    in3Monaten.setMonth(in3Monaten.getMonth() + 3);

    forderungen.forEach(f => {
      nachStatus[f.status] = (nachStatus[f.status] || 0) + 1;
      if (f.versicherung_gegner) {
        if (!nachVers[f.versicherung_gegner]) nachVers[f.versicherung_gegner] = { betrag: 0, anzahl: 0 };
        nachVers[f.versicherung_gegner].betrag += f.gesamtbetrag || 0;
        nachVers[f.versicherung_gegner].anzahl += 1;
      }
    });

    const verjBald = forderungen.filter(f => {
      if (!f.verjährungsfrist) return false;
      const vf = new Date(f.verjährungsfrist);
      return vf > heute && vf <= in3Monaten;
    });

    return {
      gesamt_offen: offene.length,
      gesamt_betrag: offene.reduce((s, f) => s + (f.gesamtbetrag || 0), 0),
      nach_versicherung: Object.entries(nachVers).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.betrag - a.betrag),
      nach_status: Object.entries(nachStatus).map(([status, anzahl]) => ({ status, anzahl })),
      verjährung_bald: verjBald,
      durchschnitt_zahlungsdauer: (() => {
        const erledigte = forderungen.filter(f => ['erledigt', 'abgerechnet'].includes(f.status) && f.created_at && f.updated_at);
        if (erledigte.length === 0) return 0;
        const totalTage = erledigte.reduce((sum, f) => {
          const start = new Date(f.created_at).getTime();
          const end = new Date(f.updated_at).getTime();
          return sum + Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
        }, 0);
        return Math.round(totalTage / erledigte.length);
      })(),
    };
  }, [forderungen]);

  return {
    forderungen, loading, error,
    fetchForderungen, createForderung, updateForderung, deleteForderung,
    getEintraege, addEintrag,
    getMahnungen, addMahnung,
    getDokumente, addDokument,
    getStatistiken,
  };
}
